//============================================================================
// Name        : Receive.tsx
// Purpose     : Implement Warehouse receive UI
// Created     : 2019-06-25
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line: no-unused-expression
/** @jsx jsx */ jsx;
import { jsx } from "@emotion/core";
import moment from "moment";
import React, {RefObject} from "react";
import { connect } from "react-redux";
import { IUpdateStateAction } from "./actions/receiveActions";
import ConfirmDialog from "./ConfirmDialog";
import DatePicker from "./DatePicker";
import ErrorDialog from "./ErrorDialog";
import ReceiveItem from "./ReceiveItem";
import ReceiveStatus from "./ReceiveStatus";
import { IReceiveProps, ReceiveActions } from "./types";

class Receive extends React.Component<IReceiveProps> {
    postingDateField: RefObject<DatePicker>;

    constructor(props: IReceiveProps) {
        super(props);
        // Save properties sent from Rails in the wrapper into the store
        this.props.storeReceiveProps(props);
        this.postingDateField = React.createRef();
    }

    onPostingDateChange = (date: string) => {
        const newState = { postingDate: date };

        // Show confirm date change dialog
        this.props.showChangeDateConfirmation(
            () => {
                this.props.updateState(newState);
                this.props.hideCancelConfirmation();
            },
            () => {
                this.postingDateField.current.undoValueUpdate();
                this.props.hideCancelConfirmation();
            }
        );
    }

    receivingCallback = () => null;
    stopReceivingCallback = async () => {
        this.props.cancelReceive();
    }

    showCancelConfirm = () => {
        this.props.showCancelConfirmation(this.stopReceivingCallback, this.props.hideCancelConfirmation);
    }

    render() {
        const maxDate = moment(new Date()).format("YYYY-MM-DD");
        return (
            <React.Fragment>
                <div>
                    <div className="receive-overlay-wrapper" style={{ display: "none" }}></div>
                    <div className="mf-table table-responsive col-md-12">
                        <table className="table table-bordered table-striped">
                            <thead className="thead-inverse">
                            <tr>
                                <th className={"text-center"}>PO</th>
                                <th>Vendor</th>
                                <th className={"text-center"}>Posting Date</th>
                                <th className={"text-center"}>Action</th>
                            </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={"text-center"}>{this.props.receivingId}</td>
                                    <td>{this.props.vendorName}</td>
                                    <td className={"text-center"}><DatePicker ref={this.postingDateField} maxDate={maxDate} value={this.props.postingDate} onDateChanged={this.onPostingDateChange} readOnly={this.props.invoiced || this.props.isReceiving} /></td>
                                    <td className={"text-center"}>
                                        {!this.props.isReceiving && !this.props.invoiced && <button onClick={this.props.continue} className="man-btn man-green-btn">Continue</button>}
                                        {this.props.isReceiving && !this.props.invoiced && <button onClick={this.showCancelConfirm} className="man-btn man-red-btn">Cancel</button>}
                                        {this.props.invoiced && <span className="label label-success">INVOICED</span>}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ErrorDialog title={this.props.errorTitle} message={this.props.errorMsg} />

                    <ConfirmDialog
                        show={this.props.showConfirmation}
                        title={this.props.confirmationTitle}
                        message={this.props.confirmationMsg}
                        positiveBtnLbl="Yes"
                        negativeBtnLbl="No"
                        positiveBtnCallback={this.props.confirmationDlgPositiveCallback}
                        negativeBtnCallback={this.props.confirmationDlgNegativeCallback} />

                    {this.props.validDates && this.props.isReceiving && <div>
                        <div className="mf-table-receiving-wrapper">
                            <div className="mf-table mf-table-receiving table-responsive col-md-12">
                                <table className="table table-bordered table-striped ">
                                    <thead className="thead-inverse">
                                    <tr>
                                    <th>Item #</th>
                                    <th>Description</th>
                                    <th>UOM</th>
                                    <th>PO QTY</th>
                                    <th></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {this.props.reception.external_receive_items.map((item, key) =>
                                        <ReceiveItem reception={this.props.reception} externalItem={item.id} key={key}
                                            receiveCallback={this.receivingCallback}
                                            stopCallback={this.stopReceivingCallback} />
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>}
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = state => ({
    isReceiving:          state.receiveOrders.isReceiving,

    actualReceiptDate:    state.receiveOrders.actualReceiptDate,
    postingDate:          state.receiveOrders.postingDate,
    validDates:           state.receiveOrders.validDates,

    receivingId:          state.receiveOrders.reception.receiving_id,
    vendorName:           state.receiveOrders.reception.vendor_name,
    status:               state.receiveOrders.reception.status,
    invoiced:             state.receiveOrders.reception.invoiced,

    showConfirmation:     state.receiveOrders.showConfirmation,
    confirmationTitle:    state.receiveOrders.confirmationTitle,
    confirmationMsg:      state.receiveOrders.confirmationMsg,
    confirmationDlgPositiveCallback: state.receiveOrders.confirmationDlgPositiveCallback,
    confirmationDlgNegativeCallback: state.receiveOrders.confirmationDlgNegativeCallback,

    errorTitle:           state.receiveOrders.errorTitle,
    errorMsg:             state.receiveOrders.errorMsg
});

const mapDispatchToProps = dispatch => ({
    storeReceiveProps: (props: IReceiveProps) => dispatch({
        type: ReceiveActions.StoreReceiveProps,
        payload: props
    } as IUpdateStateAction),
    updateState: (newState: object) => dispatch({
        type: ReceiveActions.UpdateState,
        payload: newState
    } as IUpdateStateAction),
    continue: () => dispatch({
        type: ReceiveActions.Continue
    }),
    cancelReceive: () => dispatch({
        type: ReceiveActions.CancelReceive
    }),
    showCancelConfirmation: (positiveCb, negativeCb) => dispatch({
        type: ReceiveActions.ShowCancelConfirmation,
        payload: { positiveCb, negativeCb }
    }),
    hideCancelConfirmation: () => dispatch({
        type: ReceiveActions.HideCancelConfirmation
    }),
    showChangeDateConfirmation: (positiveCb, negativeCb) => dispatch({
        type: ReceiveActions.ShowChangeDateConfirmation,
        payload: { positiveCb, negativeCb }
    })
});

export default connect(mapStateToProps, mapDispatchToProps)(Receive);
